/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimateTimings, AnimationAnimateMetadata, AnimationGroupMetadata, AnimationKeyframesSequenceMetadata, AnimationMetadata, AnimationMetadataType, AnimationSequenceMetadata, AnimationStateMetadata, AnimationStyleMetadata, AnimationTransitionMetadata, sequence} from '@angular/animations';

import {normalizeStyles, parseTimeExpression} from '../util';

import {AnimationDslVisitor, visitAnimationNode} from './animation_dsl_visitor';

export type StyleTimeTuple = {
  startTime: number; endTime: number;
};
/**
 * @param {?} ast
 * @return {?}
 */
export function validateAnimationSequence(ast: AnimationMetadata) {
  const /** @type {?} */ normalizedAst =
      Array.isArray(ast) ? sequence( /** @type {?} */((<AnimationMetadata[]>ast))) : /** @type {?} */(( <AnimationMetadata>ast));
  return new AnimationValidatorVisitor().validate(normalizedAst);
}
export class AnimationValidatorVisitor implements AnimationDslVisitor {
/**
 * @param {?} ast
 * @return {?}
 */
validate(ast: AnimationMetadata): string[] {
    const /** @type {?} */ context = new AnimationValidatorContext();
    visitAnimationNode(this, ast, context);
    return context.errors;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitState(ast: AnimationStateMetadata, context: any): any {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitTransition(ast: AnimationTransitionMetadata, context: any): any {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSequence(ast: AnimationSequenceMetadata, context: AnimationValidatorContext): any {
    ast.steps.forEach(step => visitAnimationNode(this, step, context));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitGroup(ast: AnimationGroupMetadata, context: AnimationValidatorContext): any {
    const /** @type {?} */ currentTime = context.currentTime;
    let /** @type {?} */ furthestTime = 0;
    ast.steps.forEach(step => {
      context.currentTime = currentTime;
      visitAnimationNode(this, step, context);
      furthestTime = Math.max(furthestTime, context.currentTime);
    });
    context.currentTime = furthestTime;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitAnimate(ast: AnimationAnimateMetadata, context: AnimationValidatorContext): any {
    // we reassign the timings here so that they are not reparsed each
    // time an animation occurs
    context.currentAnimateTimings = ast.timings =
        parseTimeExpression( /** @type {?} */((<string|number>ast.timings)), context.errors);

    const /** @type {?} */ astType = ast.styles && ast.styles.type;
    if (astType == AnimationMetadataType.KeyframeSequence) {
      this.visitKeyframeSequence( /** @type {?} */((<AnimationKeyframesSequenceMetadata>ast.styles)), context);
    } else {
      context.currentTime +=
          context.currentAnimateTimings.duration + context.currentAnimateTimings.delay;
      if (astType == AnimationMetadataType.Style) {
        this.visitStyle( /** @type {?} */((<AnimationStyleMetadata>ast.styles)), context);
      }
    }

    context.currentAnimateTimings = null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitStyle(ast: AnimationStyleMetadata, context: AnimationValidatorContext): any {
    const /** @type {?} */ styleData = normalizeStyles(ast.styles);
    const /** @type {?} */ timings = context.currentAnimateTimings;
    let /** @type {?} */ endTime = context.currentTime;
    let /** @type {?} */ startTime = context.currentTime;
    if (timings && startTime > 0) {
      startTime -= timings.duration + timings.delay;
    }
    Object.keys(styleData).forEach(prop => {
      const /** @type {?} */ collectedEntry = context.collectedStyles[prop];
      let /** @type {?} */ updateCollectedStyle = true;
      if (collectedEntry) {
        if (startTime != endTime && startTime >= collectedEntry.startTime &&
            endTime <= collectedEntry.endTime) {
          context.errors.push(
              `The CSS property "${prop}" that exists between the times of "${collectedEntry.startTime}ms" and "${collectedEntry.endTime}ms" is also being animated in a parallel animation between the times of "${startTime}ms" and "${endTime}ms"`);
          updateCollectedStyle = false;
        }

        // we always choose the smaller start time value since we
        // want to have a record of the entire animation window where
        // the style property is being animated in between
        startTime = collectedEntry.startTime;
      }
      if (updateCollectedStyle) {
        context.collectedStyles[prop] = {startTime, endTime};
      }
    });
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyframeSequence(
      ast: AnimationKeyframesSequenceMetadata, context: AnimationValidatorContext): any {
    let /** @type {?} */ totalKeyframesWithOffsets = 0;
    const /** @type {?} */ offsets: number[] = [];
    let /** @type {?} */ offsetsOutOfOrder = false;
    let /** @type {?} */ keyframesOutOfRange = false;
    let /** @type {?} */ previousOffset: number = 0;
    ast.steps.forEach(step => {
      const /** @type {?} */ styleData = normalizeStyles(step.styles);
      let /** @type {?} */ offset = 0;
      if (styleData.hasOwnProperty('offset')) {
        totalKeyframesWithOffsets++;
        offset = /** @type {?} */(( <number>styleData['offset']));
      }
      keyframesOutOfRange = keyframesOutOfRange || offset < 0 || offset > 1;
      offsetsOutOfOrder = offsetsOutOfOrder || offset < previousOffset;
      previousOffset = offset;
      offsets.push(offset);
    });

    if (keyframesOutOfRange) {
      context.errors.push(`Please ensure that all keyframe offsets are between 0 and 1`);
    }

    if (offsetsOutOfOrder) {
      context.errors.push(`Please ensure that all keyframe offsets are in order`);
    }

    const /** @type {?} */ length = ast.steps.length;
    let /** @type {?} */ generatedOffset = 0;
    if (totalKeyframesWithOffsets > 0 && totalKeyframesWithOffsets < length) {
      context.errors.push(`Not all style() steps within the declared keyframes() contain offsets`);
    } else if (totalKeyframesWithOffsets == 0) {
      generatedOffset = 1 / length;
    }

    const /** @type {?} */ limit = length - 1;
    const /** @type {?} */ currentTime = context.currentTime;
    const /** @type {?} */ animateDuration = /** @type {?} */(( context.currentAnimateTimings)).duration;
    ast.steps.forEach((step, i) => {
      const /** @type {?} */ offset = generatedOffset > 0 ? (i == limit ? 1 : (generatedOffset * i)) : offsets[i];
      const /** @type {?} */ durationUpToThisFrame = offset * animateDuration;
      context.currentTime =
          currentTime + /** @type {?} */(( context.currentAnimateTimings)).delay + durationUpToThisFrame; /** @type {?} */((
      context.currentAnimateTimings)).duration = durationUpToThisFrame;
      this.visitStyle(step, context);
    });
  }
}
export class AnimationValidatorContext {
public errors: string[] = [];
public currentTime: number = 0;
public currentAnimateTimings: AnimateTimings|null;
public collectedStyles: {[propName: string]: StyleTimeTuple} = {};
}

function AnimationValidatorContext_tsickle_Closure_declarations() {
/** @type {?} */
AnimationValidatorContext.prototype.errors;
/** @type {?} */
AnimationValidatorContext.prototype.currentTime;
/** @type {?} */
AnimationValidatorContext.prototype.currentAnimateTimings;
/** @type {?} */
AnimationValidatorContext.prototype.collectedStyles;
}

